/* eslint-disable default-param-last */
/* eslint-disable no-param-reassign */
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import uniqWith from 'lodash/uniqWith';
import {urls} from '~/common/evite/urls';
import {guestsApi} from '~/common/fabric/EventGuests/api/endpoints/guests';
import {contactsApi} from '~/common/fabric/EventGuests/api/endpoints/contacts';
import {
  contactsParams,
  guestListParams,
  paginatedResponseInit,
  updateGuests,
  updateGuestsUI,
} from '~/common/fabric/EventGuests/reducers/guests';
import {toastAlert} from '~library/atoms/Toast';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {Typography} from '~library/atoms/Typography';
import {parse_us_phone_number as isPhone, valid_email as isEmail, pluralize} from '~/common/utils';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {selectEventId} from '~/common/fabric/EventGuests/reducers/guests/selectors';
import {repopulateContacts} from '~/common/fabric/EventGuests/Guests/GuestSearchSuggestions/useContactsWorker';

import {emeraldWebText} from '~libSass/base/_exports.sass';

const alert = ({title, body, type, icon}) =>
  toastAlert(
    <span>
      <Typography as="span" variant="paragraph2" style={{display: 'block'}}>
        {title}
      </Typography>
      <Typography as="span" variant="paragraph2" style={{display: 'block'}}>
        {body}
      </Typography>
    </span>,
    {
      type,
      icon,
      position: 'top-right',
      autoClose: 2500,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    }
  );

export const selectContactsParams = (state) => {
  const {contact_group_id: contactGroupId, contactsParams: params} = state?.guests || {};
  return {
    ...params,
    contact_group_id: contactGroupId,
  };
};

export const selectImportParams = (state) => {
  const {params} = state?.guests || {};

  return {
    ...params,
    q: params?.import_search_string || '',
  };
};

export const fetchGuestsImportThunk =
  (p = {}, returnAll = false) =>
  async (dispatch, getState) => {
    const {origin} = window.location;
    const eventId = selectEventId(getState());

    const url = new URL(`/ajax/event/${eventId}/guestlist/import/`, origin);
    const params = {...p};

    if (returnAll) {
      params.page = 1;
      params.per_page = Number.MAX_SAFE_INTEGER;
    }

    const searchParams = new URLSearchParams({
      ...selectImportParams(getState()),
      ...params,
    });

    url.search = searchParams.toString();

    const resp = await evite.fetch(url, {
      method: 'get',
      credentials: 'include',
      headers: {Accept: 'application/json'},
    });
    const {guests: imports, letters} = await resp.json();

    dispatch(updateGuestsUI({loadingAddressBook: false}));
    if (returnAll) {
      return imports;
    }
    dispatch(updateGuests({imports, letters}));
    return undefined;
  };

export const fetchContactsThunk =
  (p = {}, returnAll = false) =>
  async (dispatch, getState) => {
    const eventId = selectEventId(getState());
    const {origin} = window.location;
    const url = new URL(`/ajax/event/${eventId}/guestlist/contacts/`, origin);
    const params = {...p};

    if (returnAll) {
      params.page = 1;
      params.per_page = Number.MAX_SAFE_INTEGER;
    }

    const searchParams = new URLSearchParams({
      ...selectContactsParams(getState()),
      ...params,
    });
    url.search = searchParams.toString();

    const resp = await evite.fetch(url, {
      method: 'get',
      credentials: 'include',
      headers: {Accept: 'application/json'},
    });

    const responseData = await resp.json();
    let {contacts} = responseData;
    const {letters, all_are_guests: allAreGuests, contact_groups: contactGroups} = responseData;

    // NOTE: I think this set's contacts (in redux) to undefined...
    if (!contacts) {
      contacts = {
        page: [],
        current_page: '1',
        has_next: false,
        count: 0,
        num_pages: 0,
      };
    }

    if (returnAll) {
      return contacts;
    }
    dispatch(
      updateGuests({contacts, letters, all_are_guests: allAreGuests, contact_groups: contactGroups})
    );
    return undefined;
  };

export const fetchGuestsListThunk =
  (params = {}, getAll = false) =>
  async (dispatch, getState) => {
    const state = getState();
    const eventId = selectEventId(state);
    const inEditMode = eventInEditMode(state);

    const updatedParams = {
      ...state.guests.guestListParams,
      ...params,
    };

    if (getAll) {
      updatedParams.page = 1;
      updatedParams.per_page = Number.MAX_SAFE_INTEGER;
    }

    if (!isNil(params)) {
      dispatch(updateGuests({guestListParams: updatedParams}));
    }

    dispatch(
      guestsApi.endpoints.fetchGuests.initiate(
        {eventId, searchParams: updatedParams, inEditMode},
        {forceRefetch: true}
      )
    );
  };

export const checkIfContainsRecipients = async (eventId) => {
  const {origin} = window.location;
  const url = new URL(`/ajax/event/${eventId}/guestlist/draft/`, origin);
  const searchParams = new URLSearchParams({...guestListParams, per_page: 1});
  url.search = searchParams.toString();

  const resp = await evite.fetch(url, {
    method: 'get',
    credentials: 'include',
    headers: {Accept: 'application/json'},
  });

  const {
    guests: {count},
  } = await resp.json();

  return !!count;
};

export const addGuestsThunk =
  (guests = []) =>
  async (dispatch, getState) => {
    const eventId = selectEventId(getState());
    const {ui} = getState().guests;

    const {request: req, adding} = ui;

    try {
      dispatch(
        updateGuestsUI({adding: [...adding, guests?.[0]?.id], request: {...req, pending: true}})
      );

      await dispatch(guestsApi.endpoints.addNewGuestsOptimistic.initiate({eventId, guests}));

      await dispatch(fetchGuestsImportThunk());

      dispatch(updateGuestsUI({adding: [], request: {...req, pending: false}}));
      window.setTimeout(() => repopulateContacts(eventId), 400);
    } catch (e) {
      evite.error('Error in posting guest');
      evite.error(e);
    }
  };

export const deleteGuestThunk = (guestId) => async (dispatch, getState) => {
  const {
    ui,
    guests: {page, current_page: currentPage},
  } = getState().guests;
  const eventId = selectEventId(getState());
  const {request: req, deleting} = ui;

  try {
    dispatch(
      updateGuestsUI({
        deleting: [...deleting, guestId],
        request: {...req, pending: true},
      })
    );

    await dispatch(guestsApi.endpoints.deleteGuest.initiate({eventId, guestId}));

    let currPage = Number(currentPage);
    const previousPage = currPage - 1;
    if (page && page.length - 1 <= 0) {
      if (previousPage <= 0) {
        currPage = 1;
      } else {
        currPage = previousPage;
      }
    }
    await dispatch(fetchGuestsListThunk({page: currPage}));

    await dispatch(fetchContactsThunk());
    await dispatch(fetchGuestsImportThunk());

    const payload = {deleting: [], request: {...req, pending: false}};
    dispatch(updateGuestsUI(payload));
    window.setTimeout(() => repopulateContacts(eventId), 400);
  } catch (e) {
    evite.error('Error in posting guest', e);
  }
};

export const saveNewContactListToGuestThunk = (contact) => async (dispatch, getState) => {
  const url = urls.get('ajax_profile_contacts_list');
  const {fabricGreetingCard} = getState().guests;
  const label = fabricGreetingCard ? 'Recipient' : 'Guest';

  try {
    const resp = await evite.fetch(url, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(contact),
      headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    });
    const guestList = await resp.json();
    const guests = guestList.map((guest) => ({
      ...guest,
      invite_method: contact.phone ? 'sms' : 'email',
      phone: contact.phone,
      selected: true,
      import_method: 'paste_list',
    }));
    await dispatch(addGuestsThunk(guests));
    dispatch(updateGuests({q: ''}));
    dispatch(updateGuestsUI({pasteGuestList: false}));

    alert({
      title: 'Success',
      body: `${pluralize(label, guests.length)} Added.`,
      type: 'success',
      icon: <CheckmarkIcon ratio={0.65} color={emeraldWebText} />,
    });
    window.setTimeout(() => repopulateContacts(getState().eviteEvent?.id), 400);
  } catch (e) {
    evite.error('Error in creating contact', e);
  }
};

export const saveNewContactToGuestThunk =
  (contact, hideToast = false) =>
  async (dispatch, getState) => {
    const url = '/ajax/profile/contacts';
    const {fabricGreetingCard} = getState().guests;
    const label = fabricGreetingCard ? 'recipient' : 'Guest';

    try {
      dispatch(updateGuests({addingGuest: true}));

      const resp = await evite.fetch(url, {
        method: 'PUT',
        credentials: 'include',
        body: JSON.stringify(contact),
        headers: {Accept: 'application/json'},
      });

      const guest = await resp.json();
      const guests = [
        {
          ...guest,
          invite_method: contact.phone ? 'sms' : 'email',
          phone: contact.phone,
          selected: true,
          import_method: 'manually_added',
        },
      ];
      await dispatch(addGuestsThunk(guests));
      await dispatch(
        updateGuestsUI({
          adding: [],
          name: '',
          email: '',
          phone: '',
          creating: false,
        })
      );

      if (!hideToast) {
        alert({
          title: 'Success',
          body: `1 ${label} added.`,
          type: 'success',
          icon: <CheckmarkIcon ratio={0.65} color={emeraldWebText} />,
        });
      }
    } catch (e) {
      evite.error('Error in creating contact');
      evite.error(e);
    } finally {
      dispatch(updateGuests({q: '', addingGuest: false}));
    }
  };

export const saveNewContactToGuest = (dispatch, guest, hideToast) =>
  dispatch(saveNewContactToGuestThunk(guest, hideToast));

const fetchUrlAndAlertUserInCaseOfError = async (url, headers, body, label) => {
  const result = await fetch(url, {method: 'POST', headers, body});
  const payload = await result.json();
  if (!result.ok) {
    alert({
      title: `Unable to add ${label}`,
      body: `0 ${label} added. Error: ${payload.message ?? result.statusText}`,
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} />,
    });
    throw new Error(payload.message || result.statusText);
  }
};

export const uploadGuests = (file) => async (dispatch, getState) => {
  const eventId = selectEventId(getState());
  const {fabricGreetingCard} = getState().guests;
  try {
    const token = window.evite.cookie('csrftoken');
    const body = new FormData();

    body.append('csrfmiddlewaretoken', token);
    body.append('csv-upload', file);

    const url1 = '/profile/contacts/import/csv';
    const url2 = `/event/${eventId}/guests/csv/draft`;

    const headers = {
      'X-CSRFToken': token,
      accept: 'application/json, */*',
    };

    const label = fabricGreetingCard ? 'recipient' : 'Guest';
    await fetchUrlAndAlertUserInCaseOfError(url1, headers, body, label);
    await fetchUrlAndAlertUserInCaseOfError(url2, headers, body, label);

    dispatch(fetchContactsThunk());
    dispatch(fetchGuestsListThunk());
    dispatch(updateGuestsUI({uploading: false}));

    window.setTimeout(() => repopulateContacts(eventId), 400);
  } finally {
    dispatch(updateGuestsUI({isUploading: false}));
  }
};

export const deleteImportsThunk = () => async (dispatch, getState) => {
  const eventId = selectEventId(getState());

  const url = `/ajax/event/${eventId}/guestlist/import/all`;

  await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: {Accept: 'application/json'},
  });

  dispatch(updateGuests({imports: paginatedResponseInit}));
  dispatch(updateGuestsUI({importing: false, googleAddressBook: false}));
};

export const deleteAllContactsFromGuestlistThunk = () => async (dispatch, getState) => {
  const eventId = selectEventId(getState());

  await dispatch(contactsApi.endpoints.deleteContacts.initiate({eventId}));
  await dispatch(contactsApi.endpoints.deleteImports.initiate({eventId}));
  await dispatch(contactsApi.endpoints.deleteAllDraftContacts.initiate({eventId}));
  window.setTimeout(() => repopulateContacts(eventId), 400);
};

export const toggleAllContactsThunk = (contacts) => async (dispatch, getState) => {
  const {guestsToBeAdded} = getState().guests;

  const guests = contacts
    ?.filter(
      (contact) =>
        !(
          contact?.selected === true ||
          contact?.invited === true ||
          contact?.is_invited === true ||
          (contact?.invite_method === 'sms' && !isPhone(contact.phone).is_valid) ||
          (contact?.invite_method === 'email' && !isEmail(contact?.email).is_valid)
        )
    )
    ?.map((contact) => ({
      ...contact,
      invite_method: contact?.invite_method || (contact.email ? 'email' : 'sms'),
    }));

  const newGuestsToBeAdded = uniqWith([...guestsToBeAdded, ...guests], isEqual);

  dispatch(updateGuests({guestsToBeAdded: newGuestsToBeAdded}));
};

export const fetchEventsThunk =
  (paginationParams = {}) =>
  async (dispatch, getState) => {
    const {origin} = window.location;
    const {
      eventsParams: {sort, ...params},
    } = getState().guests;

    // ajax_event_pagination
    const url = new URL(`/ajax/profile/events/get`, origin);

    const sortTypes = {
      event_date_desc: {
        property: 'event_date',
        order: 'desc',
      },
      event_date_asc: {
        property: 'event_date',
        order: 'asc',
      },
      title_desc: {
        property: 'title',
        order: 'desc',
      },
      title_asc: {
        property: 'title',
        order: 'asc',
      },
    };

    const resp = await evite.fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-CSRFToken': evite.cookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...params,
        ...paginationParams,
        sort: sortTypes[sort],
      }),
    });

    const events = await resp.json();

    dispatch(updateGuests({events}));

    return events;
  };

export const selectAllContacts = () => async (dispatch) => {
  const {page} = await dispatch(fetchContactsThunk({}, true));
  await dispatch(toggleAllContactsThunk(page));
};

export const selectAllImportContacts = () => async (dispatch) => {
  const {page} = await dispatch(fetchGuestsImportThunk({}, true));
  await dispatch(toggleAllContactsThunk(page));
};

export const fetchEventGuestListThunk =
  (paginationParams = {}, returnAll = false) =>
  async (dispatch, getState) => {
    const {origin} = window.location;
    const eventId = selectEventId(getState());
    const params = getState().guests.eventGuestListParams;

    if (!isNil(paginationParams)) {
      dispatch(
        updateGuests({
          eventGuestListParams: {
            ...params,
            ...paginationParams,
          },
        })
      );
    }

    if (!params?.source_event_id) {
      return undefined;
    }

    // ajax_event_pagination
    const url = new URL(`/ajax/event/${eventId}/guestlist/import/event/`, origin);

    const body = {...params, ...paginationParams};

    if (body.rsvp === 'all') {
      body.rsvp = null;
    }

    if (returnAll) {
      body.page = 1;
      body.per_page = Number.MAX_SAFE_INTEGER;
    }

    const resp = await evite.fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-CSRFToken': evite.cookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    let eventGuestList = await resp.json();

    if (resp.status !== 200) {
      eventGuestList = paginatedResponseInit;
    }

    if (returnAll) {
      return eventGuestList;
    }

    await dispatch(
      updateGuests({
        eventGuestList,
      })
    );
    return undefined;
  };

export const selectAllEventGuests = () => async (dispatch) => {
  const {page} = await dispatch(fetchEventGuestListThunk({}, true));
  await dispatch(toggleAllContactsThunk(page));
};

export const addBulkGuestsThunk = () => async (dispatch, getState) => {
  dispatch(
    updateGuests({
      addingGuest: true,
    })
  );
  const eventId = selectEventId(getState());
  const {guestsToBeAdded, fabricGreetingCard} = getState().guests;

  const cleanGuestsToBeAdded = () => {
    dispatch(
      updateGuests({
        guestsToBeAdded: [],
        addingGuest: false,
        contactsParams,
        contact_group_id: '',
      })
    );

    dispatch(updateGuestsUI({eventGuestList: false}));
  };

  const validGuestsToBeAdded = guestsToBeAdded?.filter(
    (contact) =>
      !!(
        (contact.invite_method === 'email' && isEmail(contact.email).is_valid) ||
        (contact.invite_method === 'sms' && isPhone(contact.phone).is_valid)
      )
  );

  const label = fabricGreetingCard ? 'recipient' : 'Guest';

  if (validGuestsToBeAdded?.length === 0) {
    cleanGuestsToBeAdded();

    return alert({
      title: 'Fail',
      body: `0 ${label} added.`,
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} />,
    });
  }

  const response = await dispatch(
    guestsApi.endpoints.addNewGuests.initiate({eventId, guests: validGuestsToBeAdded})
  );
  const {data} = response;

  if (!response.data) {
    const e = data.message || `Unknown error when adding ${label}`;
    evite.error('Failed Response', e);
    return alert({
      title: 'Fail',
      body: e,
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} />,
    });
  }

  const validGuestsAdded = data.guests;

  await dispatch(fetchContactsThunk());
  cleanGuestsToBeAdded();

  if (!isEmpty(validGuestsAdded))
    alert({
      title: 'Success',
      body: (
        <>
          {validGuestsAdded?.length} {label}
          {validGuestsAdded?.length > 1 ? 's ' : ' '}
          added.
        </>
      ),
      type: 'success',
      icon: <CheckmarkIcon ratio={0.65} color={emeraldWebText} />,
    });
  window.setTimeout(() => repopulateContacts(eventId), 400);
  return undefined;
};
